import { type CSSProperties, type ReactNode } from "react";
import { usePinnedColumnEdge } from "@/lib/gridPinned";
import { SkyBadge, SkyButton, SkyDataGrid, SkyText, SkyTooltip } from "@skypointcloud/skypoint-ui";
import type { SkyDataGridColumn } from "@skypointcloud/skypoint-ui";
import { ROLE_LABELS } from "@shared/permissions";
import { mfaModeCovers, type MfaMode } from "@shared/types/mfa";
import type { SignInMethod } from "@shared/types/signInMethod";
import type { AccessPerson, AccessRow, AccessScope } from "@/hooks/use-access";
import { LIST_GRID_PAGINATION, pixelColumnLayout } from "@/lib/grid";
import { NO_TIME, formatInstant, instantHoverTitle } from "@/lib/time/displayTime";
import { endedWhen } from "@/lib/runs/runActivity";
import { SIGN_IN_WAIT_LABEL, signInWait } from "@/components/access/signInWait";
import { DefinitionIcon } from "@/lib/icons";
import { useDisplayTz } from "@/hooks/use-display-tz";
import { ZoneCaption } from "@/components/shell/ZoneCaption";
import { INVITE_UNCONFIRMED_LINE, accountCreationWords } from "@shared/types/accountCreation";

/**
 * Who reaches this scope, at what role (canvas 07, "User grid").
 *
 * One row is one PERSON. The same address can hold a PlatformAdmin grant, a
 * TenantAdmin grant and a Viewer grant on this instance; a row per grant made
 * that one person read as three (owner feedback). Every grant is a chip naming
 * its role and its OWN scope (the instance or tenant it was made on, which on
 * the tenant and platform tabs is often not the selected one).
 *
 * The role column means different things per tab, so it is named for what it
 * is. On the instance tab every grant listed reaches this instance, so the
 * highest active one is what the resolver enforces here: "Effective role". The
 * tenant tab also lists grants on every OTHER instance in the tenant, and the
 * platform tab grants in every tenant, so there the same number is only the
 * "Highest role" the person holds anywhere in view, not what any one place
 * enforces.
 *
 * Revoke and reactivate stay per GRANT, on the chip: each is its own audited
 * `role_assignments` row, and taking one away must not take the others with it.
 * A grant made at a BROADER scope than the tab (a tenant or platform grant seen
 * on the instance tab) is read-only here; its chip points at the tab that owns
 * it, when the viewer can open that tab (fb-platform.md §1).
 *
 * Status is a badge AND a word, because colour alone is not a signal (WCAG AA).
 * A revoked grant keeps its chip — FR-11 deactivates access and never deletes it,
 * so the history stays readable and the chip offers Reactivate.
 *
 * Group access (2026-09-28): a directory group's role is a chip too ("Viewer via
 * skypoint-skydata"), with no Revoke, because it is not a grant: the person has it while they
 * are in the group. On the instance tab it offers "Change role", which raises the person (an
 * override, "Contributor, while in skypoint-skydata") or sets them back to the group's role.
 */

export interface UsersGridActions {
  canManage: boolean;
  onRevoke: (row: AccessRow) => void;
  onReactivate: (row: AccessRow) => void;
  onActivateUser: (row: AccessRow) => void;
  /** B-06: "Deactivate user" on the platform tab; one grant of the person carries the user id. */
  onDeactivateUser?: (row: AccessRow) => void;
  /** Round 7: "Resend invitation" on a person pending their first sign-in. */
  onResendInvite?: (row: AccessRow) => void;
  busyGrantId: string | null;
  /** The label of the scope tab the viewer can open, or null when that tab is not theirs. */
  scopeTabLabel?: (scope: AccessScope) => string | null;
  onOpenScope?: (scope: AccessScope) => void;
  /** Group access: "Change role" on a group-enrolled person, from their group chip. */
  onChangeRole?: (person: AccessPerson, groupGrant: AccessRow) => void;
}

/** Names for the scopes a grant can name, from the instances `/api/v2/me` returned. */
export interface ScopeNames {
  instances: ReadonlyMap<string, { name: string; tenantName: string }>;
  tenants: ReadonlyMap<string, string>;
}

const SCOPE_BREADTH: Record<AccessScope, number> = { instance: 0, tenant: 1, platform: 2 };

/** #1639 C0: how a person signs in, under their status. */
const SIGN_IN_METHOD_WORDS: Record<SignInMethod, string> = {
  microsoft: "Microsoft",
  email: "Email",
};

/** #1639 C0: an Email person pending, whose invitation waits for the account Skypoint creates. */
export const INVITE_WAITING_LINE =
  "Waiting for account. Send the invitation once Skypoint has created it.";

/** `grants` are the ones the row draws, so the status never contradicts the Grants cell. */
function statusOf(
  person: AccessPerson,
  grants: readonly AccessRow[],
): {
  label: string;
  color: "success" | "warning" | "danger";
} {
  if (person.userStatus === "disabled") return { label: "Deactivated user", color: "danger" };
  if (!grants.some((grant) => grant.isActive)) return { label: "Disabled", color: "danger" };
  // FR-11: an invitation email is sent, but there is nothing to accept, so the state is the
  // first sign-in, never "Invited".
  if (person.userStatus === "pending") return { label: "Pending first sign-in", color: "warning" };
  return { label: "Active", color: "success" };
}

/**
 * #1639 C1: the email sign-in account under the status badge, for a person who has not signed in:
 * "Creating account" while the runner works, or what went wrong and what to do next. Words and
 * colour together (WCAG AA), the next step in full on the line below.
 */
function AccountCreationLine({ person }: { person: AccessPerson }) {
  // #1678: once an invitation went out without an account skyData made, that line says it all.
  if (person.inviteUnconfirmed) return null;
  if (!person.accountCreation || person.idpIssuer) return null;
  const words = accountCreationWords(person.accountCreation);
  if (!words) return null;
  return (
    <span className="flex flex-col gap-0.5" data-testid={`account-creation-${person.userId}`}>
      {words.tone === "danger" ? (
        <SkyBadge appearance="tint" color="danger">
          {words.label}
        </SkyBadge>
      ) : (
        <SkyText size={200} color="muted">
          {words.label}
        </SkyText>
      )}
      {words.next ? (
        <SkyText size={200} color="muted">
          {words.next}
        </SkyText>
      ) : null}
    </span>
  );
}

/**
 * The grant's OWN scope, by name. The tenant and platform tabs list grants on
 * instances and tenants other than the selected one, so the selected
 * instance's name is never a safe default. A scope the viewer's instance list
 * does not name reads as "Other instance" or "Other tenant" rather than a
 * wrong name. On the platform tab an instance also names its tenant, because
 * instance names repeat across tenants.
 */
export function grantScopeLabel(row: AccessRow, names: ScopeNames, tab: AccessScope): string {
  if (row.scopeType === "platform") return "Platform";
  if (row.scopeType === "tenant") {
    return (row.tenantId && names.tenants.get(row.tenantId)) || "Other tenant";
  }
  const instance = row.instanceId ? names.instances.get(row.instanceId) : undefined;
  if (!instance) return "Other instance";
  return tab === "platform" ? `${instance.tenantName} / ${instance.name}` : instance.name;
}

/** "Viewer via Finance", "Viewer, while in Finance", or the role alone for a direct grant. */
function chipTitle(source: AccessRow["source"], role: string, label: string): string {
  if (source === "group") return `${role} via ${label}`;
  if (source === "override") return `${role}, while in ${label}`;
  return role;
}

/** #1273: the person's active direct grant at the same exact scope as a revoked one, if any. */
function activeRoleBeside(person: AccessPerson, revoked: AccessRow): AccessRow | null {
  if (revoked.source) return null;
  return (
    person.grants.find(
      (other) =>
        other.grantId !== revoked.grantId &&
        other.isActive &&
        !other.source &&
        other.scopeType === revoked.scopeType &&
        (revoked.scopeType === "instance"
          ? other.instanceId === revoked.instanceId
          : other.tenantId === revoked.tenantId && other.instanceId === revoked.instanceId),
    ) ?? null
  );
}

/**
 * The chip's one action: Change role on a group's chip, Go to the owning tab for an inherited
 * grant, Revoke an active grant or Reactivate a disabled one; nothing otherwise.
 */
function GrantChipAction({
  grant,
  person,
  scope,
  actions,
  changeRoleHere,
  role,
  where,
  busy,
  inherited,
}: {
  grant: AccessRow;
  person: AccessPerson;
  scope: AccessScope;
  actions: UsersGridActions;
  changeRoleHere: boolean;
  role: string;
  where: string;
  busy: boolean;
  inherited: boolean;
}) {
  if (grant.source === "group" || grant.source === "override") {
    // Not a grant to revoke: the group decides. The group chip carries "Change role".
    if (!changeRoleHere || !actions.onChangeRole) return null;
    const onChangeRole = actions.onChangeRole;
    return (
      <SkyButton
        appearance="subtle"
        size="small"
        testId={`override-role-${person.userId}`}
        aria-label={`Change the role of ${grant.email}`}
        onClick={() => onChangeRole(person, grant)}
      >
        Change role
      </SkyButton>
    );
  }
  if (inherited) {
    // Read-only here: the grant belongs to a broader tab. Point there when the
    // viewer can open it; otherwise the chip says what it is and nothing more.
    const owningTab = actions.scopeTabLabel?.(grant.scopeType) ?? null;
    if (!owningTab || !actions.onOpenScope) return null;
    const onOpenScope = actions.onOpenScope;
    return (
      <SkyButton
        appearance="subtle"
        size="small"
        testId={`open-scope-${grant.grantId}`}
        aria-label={`Go to ${owningTab} to manage ${role} (${where}) for ${grant.email}`}
        onClick={() => onOpenScope(grant.scopeType)}
      >
        Go to {owningTab}
      </SkyButton>
    );
  }
  if (actions.canManage && grant.isActive) {
    return (
      <SkyButton
        appearance="subtle"
        size="small"
        loading={busy}
        testId={`revoke-${grant.grantId}`}
        // The dialog title names the person and the scope; this button is only
        // the way in, so its own name stays short.
        aria-label={`Revoke ${role} (${where}) from ${grant.email}`}
        onClick={() => actions.onRevoke(grant)}
      >
        Revoke
      </SkyButton>
    );
  }
  if (actions.canManage && scope === "instance") {
    // Only the instance surface can put one grant back; at platform scope the
    // equivalent act is lifting the deactivation off the person.
    return (
      <SkyButton
        appearance="secondary"
        size="small"
        loading={busy}
        testId={`reactivate-${grant.grantId}`}
        aria-label={`Reactivate ${role} (${where}) for ${grant.email}`}
        onClick={() => actions.onReactivate(grant)}
      >
        Reactivate
      </SkyButton>
    );
  }
  return null;
}

function GrantChip({
  grant,
  scope,
  names,
}: {
  grant: AccessRow;
  scope: AccessScope;
  names: ScopeNames;
}) {
  const role = ROLE_LABELS[grant.roleName];
  const where = grantScopeLabel(grant, names, scope);
  const inherited = SCOPE_BREADTH[grant.scopeType] > SCOPE_BREADTH[scope];
  const label = grant.mappingLabel ?? "a directory group";
  const title = chipTitle(grant.source, role, label);
  return (
    <li
      className="flex min-w-0 flex-wrap items-center gap-2 rounded-md border border-border px-2 py-1"
      data-testid={`grant-chip-${grant.grantId}`}
    >
      <div className="flex min-w-0 flex-col">
        <SkyText size={200} weight="medium">
          {title} · {where}
        </SkyText>
        {inherited ? (
          <SkyText size={200} color="muted">
            Inherited by this {scope}
          </SkyText>
        ) : null}
      </div>
      {grant.isActive ? null : (
        <SkyBadge appearance="tint" color="danger">
          Disabled
        </SkyBadge>
      )}
    </li>
  );
}

/**
 * #1135: a row's actions live in their own pinned last column, never inside a chip, so a button
 * cannot spill over the Status cell. Each grant's action keeps its label and test id. A person
 * with more than one grant gets the grant's role and place as a muted line over its button, so
 * three "Revoke" buttons stay apart for the eye as they do by name for a screen reader.
 */
function grantActionItem({
  grant,
  person,
  scope,
  names,
  actions,
  changeRoleHere,
  showContext,
}: {
  grant: AccessRow;
  person: AccessPerson;
  scope: AccessScope;
  names: ScopeNames;
  actions: UsersGridActions;
  changeRoleHere: boolean;
  showContext: boolean;
}): ReactNode {
  const role = ROLE_LABELS[grant.roleName];
  const where = grantScopeLabel(grant, names, scope);
  // Called, not rendered: it answers null when the grant offers nothing, and then no row is drawn.
  const button = GrantChipAction({
    grant,
    person,
    scope,
    actions,
    changeRoleHere,
    role,
    where,
    busy: actions.busyGrantId === grant.grantId,
    inherited: SCOPE_BREADTH[grant.scopeType] > SCOPE_BREADTH[scope],
  });
  if (!button) return null;
  return (
    <li
      key={grant.grantId}
      className="flex min-w-0 flex-col items-start"
      data-testid={`grant-action-${grant.grantId}`}
    >
      {showContext ? (
        <SkyText size={200} color="muted">
          {role} · {where}
        </SkyText>
      ) : null}
      {button}
    </li>
  );
}

/**
 * The group chip that carries a person's "Change role": the highest group role in THIS instance
 * (the instance tab only, where the override is made), when the viewer can manage access.
 */
function changeRoleChipOf(
  person: AccessPerson,
  scope: AccessScope,
  actions: UsersGridActions,
): string | null {
  if (scope !== "instance" || !actions.canManage || !actions.onChangeRole) return null;
  const chip = person.grants.find(
    (grant) => grant.source === "group" && grant.scopeType === "instance" && grant.isActive,
  );
  return chip?.grantId ?? null;
}

/**
 * Round 7: the grant a pending person's "Resend invitation" resends on this tab: an active direct
 * grant the tab owns (the instance tab its instance grants, the tenant tab its tenant and
 * instance grants, the platform tab any), so the route that resends it is this tab's own. None
 * while the person's sign-in account is still being prepared (#1639 C1, the server refuses it too).
 */
function resendGrantOf(person: AccessPerson, scope: AccessScope): AccessRow | null {
  if (person.userStatus !== "pending") return null;
  // #1639 C1: while the email sign-in account is being made, the held invitation goes out on its
  // own once it exists; a resend now would link to an account that is not there yet.
  if (person.accountCreation?.status === "pending") return null;
  const owned = (grant: AccessRow): boolean =>
    scope === "platform" ||
    grant.scopeType === "instance" ||
    (scope === "tenant" && grant.scopeType === "tenant");
  return person.grants.find((grant) => !grant.source && grant.isActive && owned(grant)) ?? null;
}

/**
 * Each column's minimum (#1135, the contract of `client/src/lib/gridPinned.ts`): the percentages
 * share the pane in proportion, and the table has a floor (`pixelColumnLayout`). Actions is the
 * pinned last column, so a button never sits over a neighbour (a disabled grant's Reactivate sat
 * over Status at 1024 px). With Last active (a reader who manages users) the floor is 920 px on
 * every tab, inside the 927 px pane at 1024 px, so nothing sits under Actions there or from about
 * 1229 px. The platform tab's sign-in line lives in the Status cell, so it adds no column. Status
 * holds its widest badge, "Pending first sign-in" (`VALUE_WIDTH.userStatusBadge`) in 24 px of
 * padding. Headers wrap, so a narrow column does not push a header over its neighbour.
 * `grid-bounded` keeps the sideways scrollbar and the header in view on a long page.
 */
export const USERS_MIN_COLUMN_WIDTH: Record<string, number> = {
  email: 168,
  effectiveRole: 96,
  grants: 140,
  userStatus: 164,
  lastSignInAt: 112,
  lastActiveAt: 104,
  actions: 136,
};

const USERS_GRID_CLASS =
  "grid-bounded grid-pinned-last [&_table]:min-w-(--users-grid-floor) [&_th]:whitespace-normal";

/**
 * Why Last active is empty: never "0", never a blank cell (#1017). The no sign-in case is the
 * Sign-in column's own rule and words (`signInWait`); otherwise the sentence is true in every
 * case, a person who has not made a request since Last active was recorded or one stopped at an
 * MFA or re-authentication step.
 */
function noActivityReason(person: AccessPerson): string {
  const wait = signInWait(person);
  if (wait) return `${SIGN_IN_WAIT_LABEL[wait]}.`;
  return "No activity recorded yet.";
}

/**
 * #1017: Last active, the person's last authenticated request in the web app (an AI app's MCP
 * request does not count), as "2 minutes ago" with the exact time in the reader's zone on hover.
 * Recorded at most once every 5 minutes, so it can trail by that much.
 */
function lastActiveColumn(zone: string): SkyDataGridColumn<AccessPerson> {
  return {
    key: "lastActiveAt",
    header: "Last active",
    align: "right",
    renderCell: (_value, person) => {
      const relative = endedWhen(person.lastActiveAt ?? null, zone);
      if (!relative) {
        const reason = noActivityReason(person);
        return (
          <div
            className="flex flex-col items-end"
            data-testid={`last-active-${person.userId}`}
            title={reason}
          >
            <SkyText size={200} font="numeric" aria-hidden>
              {NO_TIME}
            </SkyText>
            <SkyText size={200} color="muted">
              {reason}
            </SkyText>
          </div>
        );
      }
      return (
        <span
          className={relative.includes(",") ? undefined : "whitespace-nowrap"}
          data-testid={`last-active-${person.userId}`}
        >
          <SkyText size={200} font="numeric" title={instantHoverTitle(person.lastActiveAt, zone)}>
            {relative}
          </SkyText>
        </span>
      );
    },
  };
}

/**
 * #1254: what Last sign-in means, said for every sign-in method. A SkyDataGrid header is a plain
 * string (client/AGENTS.md), so the hint sits on an info icon beside the zone caption: a SkyTooltip
 * over a tabIndex 0 trigger with the sentence in sr-only. The header stays "Last sign-in".
 */
const LAST_SIGN_IN_HINT =
  "Last sign-in is when the person last signed in. It usually does not change while that sign-in lasts. Last active shows recent use.";

function LastSignInHint() {
  return (
    <SkyTooltip content={LAST_SIGN_IN_HINT}>
      <span
        tabIndex={0}
        aria-describedby={undefined}
        className="inline-flex items-center text-muted-foreground cursor-help"
        data-testid="last-sign-in-hint"
      >
        <DefinitionIcon className="size-3.5" aria-hidden="true" />
        <span className="sr-only">{LAST_SIGN_IN_HINT}</span>
      </span>
    </SkyTooltip>
  );
}

/** A person's row actions column (#1135): the person's own buttons, then one per grant. */
function actionsColumn(
  scope: AccessScope,
  names: ScopeNames,
  actions: UsersGridActions,
  shownGrants: (person: AccessPerson) => AccessRow[],
  rowExtra?: (person: AccessPerson) => ReactNode,
): SkyDataGridColumn<AccessPerson> {
  return {
    key: "actions",
    header: "Actions",
    renderCell: (_value, person) => {
      const grants = shownGrants(person);
      const first = grants[0];
      const resendGrant =
        actions.canManage && actions.onResendInvite ? resendGrantOf(person, scope) : null;
      const changeRoleChip = changeRoleChipOf(person, scope, actions);
      const items = grants
        .map((grant) =>
          grantActionItem({
            grant,
            person,
            scope,
            names,
            actions,
            changeRoleHere: grant.grantId === changeRoleChip,
            showContext: grants.length > 1,
          }),
        )
        .filter((item) => item !== null);
      return (
        <div className="flex min-w-0 flex-col items-start gap-1">
          {actions.canManage &&
          scope === "platform" &&
          person.userStatus === "disabled" &&
          first ? (
            <SkyButton
              appearance="secondary"
              size="small"
              loading={actions.busyGrantId === first.grantId}
              testId={`activate-${person.email}`}
              aria-label={`Activate the account ${person.email}`}
              onClick={() => actions.onActivateUser(first)}
            >
              Activate user
            </SkyButton>
          ) : null}
          {actions.canManage &&
          scope === "platform" &&
          person.userStatus !== "disabled" &&
          // The server refuses a self-deactivation; the control is not offered at all.
          !person.isSelf &&
          first &&
          actions.onDeactivateUser ? (
            <SkyButton
              appearance="subtle"
              size="small"
              loading={actions.busyGrantId === first.grantId}
              testId={`deactivate-${person.email}`}
              aria-label={`Deactivate the account ${person.email}`}
              onClick={() => actions.onDeactivateUser?.(first)}
            >
              Deactivate user
            </SkyButton>
          ) : null}
          {resendGrant && actions.onResendInvite ? (
            <SkyButton
              appearance="subtle"
              size="small"
              loading={actions.busyGrantId === resendGrant.grantId}
              testId={`resend-invite-${person.userId}`}
              aria-label={`Resend invitation to ${person.email}`}
              onClick={() => actions.onResendInvite?.(resendGrant)}
            >
              Resend invitation
            </SkyButton>
          ) : null}
          {rowExtra?.(person)}
          {items.length > 0 ? (
            <ul
              className="flex min-w-0 flex-col items-start gap-1"
              aria-label={`Actions for ${person.email}`}
            >
              {items}
            </ul>
          ) : null}
        </div>
      );
    },
  };
}

export function UsersGrid({
  people,
  scope,
  names,
  actions,
  loading,
  statusExtra,
  rowExtra,
  groupAccess = false,
  mfaMode,
}: {
  /** The deployment's effective MFA mode (`GET /me` `mfa.mode`); undefined from an older server. */
  mfaMode?: MfaMode;
  people: AccessPerson[];
  scope: AccessScope;
  names: ScopeNames;
  actions: UsersGridActions;
  loading?: boolean;
  /** A line a screen adds under the Status badge (the platform tab's sign-in line, S9). */
  statusExtra?: (person: AccessPerson) => ReactNode;
  /** A button a screen adds to the Actions column (the platform tab's "Reset sign-in", S9). */
  rowExtra?: (person: AccessPerson) => ReactNode;
  /**
   * Group access is on (`GET /me` `features.groupAccess`). Off (the default, hidden by the owner
   * on 2026-10-01), a row says nothing about groups: no "Added by group", no overage warning.
   */
  groupAccess?: boolean;
}) {
  const zone = useDisplayTz();
  // #1017: the server answers `lastActiveAt` only to a reader who can manage users (owner, #1067
  // option A); for anyone else the field is absent and so is the column.
  const showLastActive = people.some((person) => person.lastActiveAt !== undefined);
  // Off, a group or override chip is not drawn either (the server already strips them; this
  // covers an older server that still sends them).
  // #1273 (owner 2026-10-16, one role per instance): a revoked direct grant beside the person's
  // active direct role at the same scope is history, not a second role, so it is not drawn and
  // not counted (the audit trail keeps it). Alone, it stays, with its Reactivate.
  const shownGrants = (person: AccessPerson) =>
    (groupAccess ? person.grants : person.grants.filter((grant) => !grant.source)).filter(
      (grant) => grant.isActive || !activeRoleBeside(person, grant),
    );
  // The Actions column only where a row can hold a control: a manager, or a reader whose
  // inherited grant has a tab to go to. A Viewer sees no empty column.
  const hasActions =
    actions.canManage ||
    (actions.onOpenScope !== undefined &&
      people.some((person) =>
        shownGrants(person).some(
          (grant) =>
            !grant.source &&
            SCOPE_BREADTH[grant.scopeType] > SCOPE_BREADTH[scope] &&
            actions.scopeTabLabel?.(grant.scopeType),
        ),
      ));
  const columns: SkyDataGridColumn<AccessPerson>[] = [
    {
      key: "email",
      header: "User",
      renderCell: (_value, person) => (
        <div className="flex min-w-0 flex-col">
          {/* An address has no spaces, so it would run into the next column, or wrap at its
              hyphen ("pa-" / "test@..."); it alone breaks anywhere. The name keeps words. */}
          {person.displayName ? (
            <>
              <SkyText size={300} weight="medium">
                {person.displayName}
                {person.isSelf ? " · you" : ""}
              </SkyText>
              <SkyText size={200} color="muted" className="break-all">
                {person.email}
              </SkyText>
            </>
          ) : (
            <>
              <SkyText size={300} weight="medium" className="break-all">
                {person.email}
              </SkyText>
              {person.isSelf ? (
                <SkyText size={200} color="muted">
                  you
                </SkyText>
              ) : null}
            </>
          )}
        </div>
      ),
    },
    {
      key: "effectiveRole",
      header: scope === "instance" ? "Effective role" : "Highest role",
      renderCell: (_value, person) => (
        <span className="text-sm" data-testid={`effective-role-${person.email}`}>
          {person.effectiveRole ? ROLE_LABELS[person.effectiveRole] : "No access"}
        </span>
      ),
    },
    {
      key: "grants",
      header: "Grants",
      renderCell: (_value, person) => (
        <ul className="flex flex-wrap gap-2" aria-label={`Grants held by ${person.email}`}>
          {shownGrants(person).map((grant) => (
            <GrantChip key={grant.grantId} grant={grant} scope={scope} names={names} />
          ))}
        </ul>
      ),
    },
    {
      key: "userStatus",
      header: "Status",
      renderCell: (_value, person) => {
        const grants = shownGrants(person);
        const status = statusOf(person, grants);
        return (
          <div className="flex flex-col items-start gap-1">
            <SkyBadge appearance="tint" color={status.color}>
              {status.label}
            </SkyBadge>
            {statusExtra?.(person)}
            {person.signInMethod ? (
              <span data-testid={`sign-in-method-${person.userId}`}>
                <SkyText size={200} color="muted">
                  {SIGN_IN_METHOD_WORDS[person.signInMethod]}
                </SkyText>
              </span>
            ) : null}
            {person.inviteWaiting ? (
              <span data-testid={`invite-waiting-${person.userId}`}>
                <SkyText size={200} color="muted">
                  {INVITE_WAITING_LINE}
                </SkyText>
              </span>
            ) : null}
            {person.inviteUnconfirmed ? (
              <span data-testid={`invite-unconfirmed-${person.userId}`}>
                <SkyText size={200} color="muted">
                  {INVITE_UNCONFIRMED_LINE}
                </SkyText>
              </span>
            ) : null}
            <AccountCreationLine person={person} />
            {groupAccess && person.createdVia === "group" ? (
              <span data-testid={`added-by-group-${person.userId}`}>
                <SkyText size={200} color="muted">
                  Added by group
                </SkyText>
              </span>
            ) : null}
            {groupAccess && person.groupsClaimState === "overage_unresolved" ? (
              <span data-testid={`groups-overage-${person.userId}`}>
                <SkyBadge appearance="tint" color="warning">
                  Too many groups in token
                </SkyBadge>
              </span>
            ) : null}
          </div>
        );
      },
    },
    {
      key: "lastSignInAt",
      header: "Last sign-in",
      align: "right",
      renderCell: (_value, person) => (
        <div className="flex flex-col items-end gap-1">
          <SkyText size={200} font="numeric" title={instantHoverTitle(person.lastSignInAt, zone)}>
            {formatInstant(person.lastSignInAt, zone, "minutes")}
          </SkyText>
          {/* Admin MFA: whether that sign-in used a second factor, by colour and label; nothing
              before the first sign-in this release recorded. */}
          {person.lastSignInAt &&
          typeof person.lastSignInMfa === "boolean" &&
          // Walk W21: "No MFA" only where the mode can ask this person for a second step; an
          // older server's `/me` names no mode, and then every badge shows, as before.
          (person.lastSignInMfa ||
            mfaMode === undefined ||
            mfaModeCovers(
              mfaMode,
              person.grants.filter((grant) => grant.isActive).map((grant) => grant.roleName),
            )) ? (
            <span
              data-testid={`last-sign-in-mfa-${person.userId}`}
              title={`Last sign-in used MFA: ${person.lastSignInMfa ? "Yes" : "No"}`}
            >
              <SkyBadge appearance="tint" color={person.lastSignInMfa ? "success" : "warning"}>
                {person.lastSignInMfa ? "MFA" : "No MFA"}
              </SkyBadge>
            </span>
          ) : null}
        </div>
      ),
    },
    ...(showLastActive ? [lastActiveColumn(zone)] : []),
    ...(hasActions ? [actionsColumn(scope, names, actions, shownGrants, rowExtra)] : []),
  ];
  const layout = pixelColumnLayout(
    // Every column is a percentage; `pixelColumnLayout` sets the real shares from the minimums.
    columns.map((column) => ({ ...column, width: column.width ?? "10%" })),
    { minimums: USERS_MIN_COLUMN_WIDTH, selection: false },
  );
  const pinnedEdge = usePinnedColumnEdge<HTMLDivElement>(true);

  const grantCount = people.reduce((total, person) => total + shownGrants(person).length, 0);

  return (
    <div className="space-y-2">
      <div ref={pinnedEdge} style={{ "--users-grid-floor": `${layout.floor}px` } as CSSProperties}>
        <SkyDataGrid<AccessPerson>
          // A floor under the percentages (post-release 607e63a): below it the grid scrolls
          // sideways in its own region instead of squeezing an address into the next column.
          className={USERS_GRID_CLASS}
          data={people}
          columns={layout.columns}
          {...LIST_GRID_PAGINATION}
          loading={loading}
          getRowId={(person) => person.userId}
          testId="users-grid"
          emptyMessage="No person matches these filters."
        />
      </div>
      {/* B-51 (round 9): Last sign-in and Last active are in the reader's zone, said once. */}
      {people.length > 0 ? (
        <div className="flex items-center gap-2">
          <ZoneCaption testId="users-zone-caption" />
          <LastSignInHint />
        </div>
      ) : null}
      <SkyText as="p" size={200} color="muted">
        {people.length === 1 ? "1 person" : `${people.length} people`},{" "}
        {grantCount === 1 ? "1 grant" : `${grantCount} grants`}. Access is deactivated, never
        deleted, so the history stays readable.
      </SkyText>
    </div>
  );
}
